import NoLocationIcon from '@mui/icons-material/WrongLocation'
import { Box, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { useMemo } from 'react'
import { Filter, isEmpty, List, LoadingPage, useGetList, useListContext } from 'react-admin'

import config from './config'
import AddressInputMain from '../../components/AddressInputMain'
import { AdvancedDatesFilter } from '../../components/AdvancedDatesFilter'
import Map, { getFormattedPosition, getMapCircle, getMapMarker, MapMarkerIcon } from '../../components/Map'
import { getRoundedNowJS } from '../../utils/dates'
import { useSingleOrganisationFilterDefaultValues } from '../accounts/hooks'
import hubsConfig from '../hubs/config'
import HubsFilter from '../hubs/filter'
import OrganisationsFilter from '../organisations/filter'
import VehiclesFilter from '../vehicles/filter'

const hasValidAddressFilter = (filterValues) => Boolean(filterValues.lat && filterValues.lng && filterValues.next_to)

const EmptyTelematicsBox = ({ isListView }) => {
  const translate = useTranslate()
  return (
    <Box justifyContent="center" alignItems="center" textAlign="center" p={3} pt={isListView ? 3 : 1.5}>
      <NoLocationIcon sx={{ fontSize: 50, color: 'text.secondary', mb: 1 }} />
      <Typography variant="h6" color="textSecondary" gutterBottom>
        {translate('resources.vehicle-telematics.list.empty')}
      </Typography>
    </Box>
  )
}

export const VehicleTelematicsFilters = ({ isListView = true, ...props }) => {
  const { filterValues } = useListContext()
  return (
    <Filter {...props}>
      <AddressInputMain
        alwaysOn
        sx={{ width: 227 }}
        latLngSources={['lat', 'lng']}
        linkedSourcesOnClear={isListView ? ['timeline_start', 'timeline_end'] : null}
        source="next_to"
      />
      {isListView && <OrganisationsFilter alwaysOn />}
      {isListView && <HubsFilter alwaysOn />}
      {isListView && <VehiclesFilter alwaysOn />}
      {(!isListView || hasValidAddressFilter(filterValues)) && (
        <AdvancedDatesFilter maxDateTime={getRoundedNowJS()} maxDurationInDays={14} alwaysOn />
      )}
    </Filter>
  )
}

export const getMarkerTypeForDataPoint = (p) => {
  const timeline = p.extra_data?.timeline
  if (timeline && timeline.length > 0) {
    if (timeline.some((e) => e.autonomy !== undefined)) return 'fueling'
    else return 'parking'
  }
  return 'normal'
}

export const VehicleTelematicsMapView = ({ height = 700, isListView = true }) => {
  const { data: vehicleTelematics, isLoading, filterValues } = useListContext()

  const { data: hubs } = useGetList(hubsConfig.name, {
    sort: hubsConfig.options.defaultSort,
    filter: {
      organisation_id: filterValues.organisation_id,
      ...(filterValues.hub_id ? { id: [filterValues?.hub_id] } : {}),
    },
  })

  const mapInfo = useMemo(() => {
    const vehicleMarkers = {}
    const vehiclePolylines = {}
    let markers = []
    let circles = []

    // --- Vehicles ---------------------------------------------------------
    if (vehicleTelematics?.length) {
      const grouped = Object.groupBy(vehicleTelematics, (t) => t.vehicle_id)

      Object.entries(grouped).forEach(([vehicleId, unified]) => {
        vehicleMarkers[vehicleId] = unified.map((item, index, array) => ({
          record: item,
          position: { lat: item.position.latitude, lng: item.position.longitude },
          Icon: MapMarkerIcon,
          markerType: getMarkerTypeForDataPoint(item),
        }))

        const getSpeedingData = (r) =>
          r.driving_events?.[0]?.event_type === 'SPEEDING' ? { ...r.driving_events[0].extra_data } : null

        if (unified.length > 1) {
          vehiclePolylines[vehicleId] = unified.map((r) => ({
            position: { lat: r.position.latitude, lng: r.position.longitude },
            speedingData: getSpeedingData(r),
            speed: r.speed ?? 0,
            max_speed: r.max_speed ?? Infinity,
            record: r,
          }))
        }
      })

      if (hasValidAddressFilter(filterValues)) {
        const searchPosition = getFormattedPosition(filterValues)
        circles.push(getMapCircle(1000, searchPosition, 'search'))
      }
    }

    // --- Hubs -------------------------------------------------------------
    if (Array.isArray(hubs) && hubs.length > 0) {
      hubs.forEach((hub) => {
        const hubMarkerGroup = getMapMarker(hub.position, 'hub', { record: hub })
        markers = markers.concat(hubMarkerGroup)
        const hubCircle = getMapCircle(hub.geofencing, hub.position)
        circles = circles.concat(hubCircle)
      })
    }

    return {
      markers: [...markers, ...Object.values(vehicleMarkers).flat()],
      polylines: Object.values(vehiclePolylines),
      circles,
    }
  }, [JSON.stringify(vehicleTelematics), JSON.stringify(filterValues), JSON.stringify(hubs)])

  // --- UI ---------------------------------------------------------------
  if (isLoading) return <LoadingPage />
  if (isEmpty(vehicleTelematics)) return <EmptyTelematicsBox isListView={isListView} />

  return <Map markers={mapInfo.markers} polylines={mapInfo.polylines} circles={mapInfo.circles} height={height} />
}

export default () => {
  const filterDefaultValues = useSingleOrganisationFilterDefaultValues()

  return (
    <List
      actions={null}
      empty={false}
      filters={<VehicleTelematicsFilters />}
      filterDefaultValues={filterDefaultValues}
      pagination={null}
      perPage={10000}
      sort={config.options.defaultSort}
    >
      <VehicleTelematicsMapView />
    </List>
  )
}
